import { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { useAuth } from '../lib/auth';
import { Palette, Plus, Edit2, Trash2 } from 'lucide-react';

interface Theme {
  id: string;
  name: string;
  description?: string;
  color?: string;
  bookCount?: number;
  createdAt?: string;
  updatedAt?: string;
  parentId?: string;
  path?: string;
  level?: number;
  children?: Theme[];
}

export function ThemesPage() {
  const { token } = useAuth();
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
  const [deletingTheme, setDeletingTheme] = useState<Theme | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3182ce',
    parentId: ''
  });
  const [flatThemes, setFlatThemes] = useState<Theme[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  type ThemeListItem = Theme & {
    computedLevel: number;
    computedPathNames: string[];
  };

  const buildThemeById = (items: Theme[]) => new Map(items.map((t) => [t.id, t] as const));

  const isDescendantOf = (candidateId: string, ancestorId: string, themeById: Map<string, Theme>): boolean => {
    // Walk upwards via parentId; protect against accidental cycles in data.
    const seen = new Set<string>();
    let current = themeById.get(candidateId)?.parentId;
    while (current) {
      if (current === ancestorId) return true;
      if (seen.has(current)) return false;
      seen.add(current);
      current = themeById.get(current)?.parentId;
    }
    return false;
  };

  const buildThemeList = (items: Theme[]): ThemeListItem[] => {
    const map = new Map<string, ThemeListItem>();
    for (const t of items) {
      map.set(t.id, { ...t, computedLevel: 0, computedPathNames: [], children: [] });
    }

    const roots: ThemeListItem[] = [];
    for (const node of map.values()) {
      if (node.parentId && map.has(node.parentId)) {
        (map.get(node.parentId)!.children as ThemeListItem[]).push(node);
      } else {
        roots.push(node);
      }
    }

    const sortByName = (a: ThemeListItem, b: ThemeListItem) => a.name.localeCompare(b.name, 'zh-CN');
    const ordered: ThemeListItem[] = [];

    const walk = (node: ThemeListItem, level: number, trail: string[]) => {
      node.computedLevel = level;
      node.computedPathNames = [...trail, node.name];
      ordered.push(node);

      const children = (node.children || []) as ThemeListItem[];
      children.sort(sortByName);
      for (const child of children) walk(child, level + 1, node.computedPathNames);
    };

    roots.sort(sortByName);
    for (const root of roots) walk(root, 0, []);

    return ordered;
  };

  const resolveErrorMessage = (err: unknown, fallback: string) => {
    const message = err && typeof err === 'object' && 'message' in err
      ? (err as { message: unknown }).message
      : err;
    if (typeof message === 'string') {
      if (message.includes('UNIQUE constraint failed') && message.includes('themes.name')) {
        return '主题名称已存在，请换一个。';
      }
      return message;
    }
    return fallback;
  };

  useEffect(() => {
    loadThemes();
  }, []);

  async function loadThemes() {
    try {
      const response = await fetch('/api/admin/themes', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to load themes');
      const data = await response.json();
      setThemes(data.themes || []); // 树形结构
      setFlatThemes(data.flatList || []); // 扁平列表，用于选择父主题
    } catch (err) {
      setError('Failed to load themes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      setError('');
      const response = await fetch('/api/admin/themes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create theme');
      }

      await loadThemes();
      setShowCreateModal(false);
      resetForm();
    } catch (err: unknown) {
      setError(resolveErrorMessage(err, '创建主题失败'));
    }
  }

  async function handleUpdate() {
    if (!editingTheme) return;

    try {
      setError('');
      const response = await fetch(`/api/admin/themes/${editingTheme.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update theme');
      }

      await loadThemes();
      setEditingTheme(null);
      resetForm();
    } catch (err: unknown) {
      setError(resolveErrorMessage(err, '更新主题失败'));
    }
  }

  async function confirmDelete(theme: Theme) {
    setDeletingTheme(theme);
    setError('');
  }

  async function handleDelete() {
    if (!deletingTheme) return;

    try {
      setError('');
      const response = await fetch(`/api/admin/themes/${deletingTheme.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'HAS_BOOKS') {
          throw new Error(error.message);
        }
        throw new Error(error.message || '删除主题失败');
      }

      await loadThemes();
      setSuccessMessage(`主题"${deletingTheme.name}"已成功删除`);
      setDeletingTheme(null);

      // 3秒后清除成功消息
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      setError(resolveErrorMessage(err, '删除主题失败'));
      // 不关闭弹窗，让用户看到错误信息
    }
  }

  function cancelDelete() {
    setDeletingTheme(null);
    setError('');
  }

  function startEdit(theme: Theme) {
    setEditingTheme(theme);
    setFormData({
      name: theme.name,
      description: theme.description || '',
      color: theme.color || '#3182ce',
      parentId: theme.parentId || ''
    });
  }

  function resetForm() {
    setFormData({
      name: '',
      description: '',
      color: '#3182ce',
      parentId: ''
    });
    setError('');
  }

  function cancelEdit() {
    setEditingTheme(null);
    resetForm();
  }

  const themeById = buildThemeById(flatThemes);
  const orderedFlatThemes = buildThemeList(flatThemes);

  // 渲染主题列表
  function renderThemeList() {
    const sortedThemes = orderedFlatThemes;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                主题名称
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                层级路径
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                书籍数量
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                描述
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                创建时间
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedThemes.map((theme) => {
              const level = theme.computedLevel;
              const pathNames = theme.computedPathNames;

              return (
                <tr key={theme.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center" style={{ paddingLeft: `${level * 24}px` }}>
                      {level > 0 && (
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                      <div
                        className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                        style={{ backgroundColor: theme.color }}
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {theme.name}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                        L{level}
                      </span>
                      {pathNames.length > 0 && (
                        <div className="text-xs text-gray-500">
                          {pathNames.map((name, index) => (
                            <span key={index}>
                              {name}
                              {index < pathNames.length - 1 && <span className="mx-1">/</span>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {theme.bookCount ? theme.bookCount : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-500 truncate max-w-xs">
                      {theme.description || '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {theme.createdAt ? new Date(theme.createdAt).toLocaleDateString('zh-CN') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => startEdit(theme)}
                      className="text-teal-600 hover:text-teal-900 mr-3"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => confirmDelete(theme)}
                      className="text-red-600 hover:text-red-900"
                      disabled={!!theme.bookCount && theme.bookCount > 0}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading themes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              <Palette className="h-4 w-4" /> 主题管理中心
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl">主题管理</h1>
            <p className="max-w-3xl text-sm text-white/90 lg:text-base">
              创建和管理图书分类主题，为书籍添加标签和色彩标识
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="rounded-2xl bg-white/15 p-6 text-center backdrop-blur-md">
              <p className="text-3xl font-bold">{themes.length}</p>
              <p className="mt-1 text-sm text-white/80">主题总数</p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm shadow-xl border border-white/30"
              size="lg"
            >
              <Plus className="mr-2 h-4 w-4" />添加主题
            </Button>
          </div>
        </div>
      </div>

      {/* 主题列表展示 */}
      <div className="space-y-6">
        {flatThemes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            暂无主题，点击上方按钮创建第一个主题
          </div>
        ) : (
          renderThemeList()
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-50 text-green-600 p-4 rounded-lg shadow-lg z-50 animate-fade-in">
          {successMessage}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingTheme && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl transform transition-all">
            <h2 className="text-xl font-bold mb-4 text-red-600">
              确认删除主题
            </h2>

            <p className="mb-4">
              确定要删除主题 "<strong>{deletingTheme.name}</strong>" 吗？
            </p>

            {deletingTheme.bookCount && deletingTheme.bookCount > 0 && (
              <div className="bg-yellow-50 text-yellow-800 p-3 rounded mb-4">
                此主题下有 {deletingTheme.bookCount} 本书，无法删除。
                请先移除书籍的主题关联。
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={!!deletingTheme.bookCount && deletingTheme.bookCount > 0}
              >
                确认删除
              </Button>
              <Button
                variant="secondary"
                onClick={cancelDelete}
              >
                取消
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTheme) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl transform transition-all">
            <h2 className="text-xl font-bold mb-4">
              {editingTheme ? '编辑主题' : '添加主题'}
            </h2>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  主题名称 *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="如：科幻小说"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  描述
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="主题描述..."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  父主题
                </label>
                <select
                  value={formData.parentId}
                  onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">无（顶级主题）</option>
                  {orderedFlatThemes
                    .filter((theme) => {
                      if (!editingTheme) return true;
                      if (theme.id === editingTheme.id) return false;
                      // 禁止将主题挂到自己的任意子孙下面（避免形成环）
                      return !isDescendantOf(theme.id, editingTheme.id, themeById);
                    })
                    .map((theme) => (
                      <option key={theme.id} value={theme.id}>
                        {'　'.repeat(theme.computedLevel)}
                        {theme.computedLevel > 0 && '└ '}
                        {theme.computedPathNames.join(' / ')}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  选择父主题后，当前主题将成为其子主题
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  颜色标识
                </label>
                <div className="flex gap-3 items-center">
                  <div className="relative">
                    <Input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-20 h-12 cursor-pointer border-2 border-gray-300 rounded-lg"
                    />
                  </div>
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#3182ce"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="flex-1 font-mono"
                  />
                  <div
                    className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-inner"
                    style={{ backgroundColor: formData.color }}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <Button
                onClick={editingTheme ? handleUpdate : handleCreate}
                disabled={!formData.name.trim()}
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg"
              >
                {editingTheme ? '更新主题' : '创建主题'}
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  if (editingTheme) {
                    cancelEdit();
                  } else {
                    setShowCreateModal(false);
                    resetForm();
                  }
                }}
              >
                取消
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
